import { Injectable } from '@angular/core';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import { DirectionKindEnum } from '@zaoblako/shared/modules/catalog/enums/direction.kind.enum';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { NdsFindInterface } from '@zaoblako/shared/modules/finance/dtos/nds-find.dto';
import type { CommentaryCreateInterface } from '@zaoblako/shared/modules/messenger/dtos/commentary/commentary-create.dto';
import type { BrsSettingsInterface } from '@zaoblako/shared/modules/saas/dtos/brs-settings.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { TaskExpenseDynamicsQueryInterface } from '@zaoblako/shared/modules/task/dtos/expense-dynamics/task.expense.dynamics.query.dto';
import type { TaskStatusFindInterface } from '@zaoblako/shared/modules/task/dtos/status/task-status-find.dto';
import type { TaskFindInterface } from '@zaoblako/shared/modules/task/dtos/task-find.dto';
import type { TaskPartialUpdateFormInterface } from '@zaoblako/shared/modules/task/dtos/task-partial-update-form.dto';
import type { TaskPriorityFindInterface } from '@zaoblako/shared/modules/task/dtos/task-priority-find.dto';
import { TaskStatusTypeEnum } from '@zaoblako/shared/modules/task/enum/task.status.type.enum';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { NdsService } from '@zaoblako/web/core/services/finance/nds.service';
import { PaymentsSettingsService } from '@zaoblako/web/core/services/finance/payments-settings.service';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { StoreService } from '@zaoblako/web/core/services/store.service';
import { TaskPriorityService } from '@zaoblako/web/core/services/task/task-priority.service';
import { TaskService } from '@zaoblako/web/core/services/task/task.service';
import { TitleService } from '@zaoblako/web/core/services/title.service';
import { KeysOfType } from '@zaoblako/web/core/types/keys-of.type';
import { TaskState } from '@zaoblako/web/modules/payments/components/task/card/models/task-state';
import { taskInitialState } from '@zaoblako/web/modules/payments/components/task/card/state/task-initial-state';
import { RequiredFieldsForApprovalFormType } from '@zaoblako/web/modules/payments/dumbs/payment-approved-task-modal/payment-approved-task-modal.component';
import { RequiredFieldsForPlannedStatusFormType } from '@zaoblako/web/modules/payments/dumbs/payment-planned-task-modal/payment-planned-task-modal.component';
import { TaskStatusStateService } from '@zaoblako/web/modules/payments/services/task-status-state.service';
import * as moment from 'moment-timezone';
import { BehaviorSubject, forkJoin, switchMap } from 'rxjs';

@Injectable()
export class TaskStateService extends StoreService<TaskState> {
  protected state = new BehaviorSubject(taskInitialState);

  public get getState() {
    return this.state.value;
  }

  public constructor(
    private readonly taskService: TaskService,
    private readonly taskStatusStateService: TaskStatusStateService,
    private readonly titleService: TitleService,
    private readonly directionService: DirectionService,
    private readonly ndsService: NdsService,
    private readonly companyService: CompanyService,
    private readonly taskPriorityService: TaskPriorityService,
    private readonly userGroupService: UserGroupService,
    private readonly paymentsSettingsService: PaymentsSettingsService
  ) {
    super();
  }

  public initState(id: number) {
    this.loadTask(id);
    this.loadPanelsInfo();
    this.loadEntities();
  }

  public loadTask(id: number): void {
    this.updateState({ loading: true });

    forkJoin([
      this.taskService.findById<TaskFindInterface>(id),
      this.paymentsSettingsService.getBrsSettings()
    ]).subscribe({
      next: ([task, brsSettings]) => {
        this.titleService.setTitle(`Заявка №${id}`);
        this.updateState({ task, brsSettings, loading: false });
      }
    });
  }

  public loadEntities() {
    forkJoin([
      this.directionService.find<DirectionFindInterface>({
        kinds: [
          DirectionKindEnum.OPERATING_EXPENSE,
          DirectionKindEnum.OTHER_EXPENSE,
          DirectionKindEnum.COMMERCIAL_EXPENSE,
          DirectionKindEnum.FINANCIAL_ACTIVITY,
          DirectionKindEnum.TECHNICAL_OPERATIONS
        ]
      }),
      this.ndsService.find<NdsFindInterface>(),
      this.companyService.find<CompanyFindInterface>(),
      this.userGroupService.find<UserGroupFindInterface>(),
      this.taskPriorityService.find<TaskPriorityFindInterface>()
    ]).subscribe(([directions, nds, organizations, userGroups, priorities]) => {
      this.updateState({
        directions,
        nds,
        organizations,
        userGroups,
        priorities
      });
    });
  }

  public updatePanelsInfo(key: KeysOfType<typeof this.getState.panelsInfo, any>, value: any) {
    const info = this.getState.panelsInfo;
    info[key] = value;
    this.updateState({ panelsInfo: info });
    localStorage.setItem('task.panelsInfo', JSON.stringify(info));
  }

  public loadPanelsInfo() {
    if (localStorage.getItem('task.panelsInfo')) {
      const panelsInfo = JSON.parse(localStorage.getItem('task.panelsInfo'));
      Object.keys(this.getState.panelsInfo).forEach((key) => {
        panelsInfo[key] = panelsInfo.hasOwnProperty(key) ? panelsInfo[key] : this.getState.panelsInfo[key];
      });
      this.updateState({ panelsInfo });
    }
  }

  public loadFiles() {
    this.updateState((state) => ({
      filesInfo: {
        loading: true,
        files: state.filesInfo.files
      }
    }));

    this.taskService.getFiles(this.getState.task?.id).subscribe({
      next: (files) => {
        this.updateState({
          filesInfo: {
            files,
            loading: false
          }
        });
      }
    });
  }

  public updateStatus(statusInfo: {
    status: TaskStatusTypeEnum;
    scheduledData?: RequiredFieldsForPlannedStatusFormType;
    approvedData?: RequiredFieldsForApprovalFormType;
    comment?: CommentaryCreateInterface;
  }) {
    const task = this.getState.task;
    let status: TaskStatusFindInterface;

    const changeTaskStatus = (task: TaskFindInterface) => {
      this.taskStatusStateService.statuses$
        .pipe(
          switchMap((statuses) => {
            status = statuses.find((_status) => _status.type === statusInfo.status);
            if (status) {
              return this.taskService.setStatus(task.id, {
                status,
                commentary: statusInfo.comment
              });
            }
          })
        )
        .subscribe((task) => {
          this.updateState({ task: { ...task, status, approvers: task.approvers } });
        });
    };

    if (statusInfo.scheduledData) {
      task.direction = statusInfo.scheduledData.direction;
      task.unit = statusInfo.scheduledData.unit;
      task.nds = statusInfo.scheduledData.nds;
      this.taskService.update(task).subscribe({
        next: () => {
          changeTaskStatus(task);
        }
      });
    } else if (statusInfo.approvedData) {
      task.contragent = statusInfo.approvedData.contragent;
      task.organization = statusInfo.approvedData.organization;
      task.direction = statusInfo.approvedData.direction;
      task.unit = statusInfo.approvedData.unit;
      this.taskService.update(task).subscribe({
        next: () => {
          changeTaskStatus(task);
        }
      });
    } else {
      changeTaskStatus(task);
    }
  }

  public changeStatus(status: TaskStatusFindInterface) {
    this.updateState({ task: { ...this.getState.task, status } });
  }

  public loadExpenseDynamics(queryParams: TaskExpenseDynamicsQueryInterface) {
    this.updateState({
      expenseDynamicsPanel: {
        items: this.getState.expenseDynamicsPanel.items,
        queryParams,
        loading: true
      }
    });

    this.taskService.expenseDynamics(this.getState.task?.id, queryParams).subscribe({
      next: (result) => {
        this.updateState({
          expenseDynamicsPanel: {
            items: result.items,
            queryParams: this.getState.expenseDynamicsPanel.queryParams,
            loading: false
          }
        });
      }
    });
  }

  public updateFilesCount(count: number) {
    this.updateState((state) => ({
      task: {
        ...state.task,
        filesCount: state.task.filesCount + count
      }
    }));
  }

  public updateBrsSettings(brsSettings: BrsSettingsInterface): void {
    this.updateState({
      brsSettings: { ...brsSettings }
    });
  }

  public updateFieldTask(key: keyof TaskPartialUpdateFormInterface, value: any) {
    if (key === 'organization') {
      this.taskService
        .patch({ id: this.getState.task?.id, organization: value || null })
        .pipe(
          switchMap(() =>
            this.taskService.patch({
              id: this.getState.task?.id,
              currency: value.currencies?.find((currency) => currency.isDefault)
            })
          )
        )
        .subscribe(() => {
          const task: TaskFindInterface = {
            ...this.getState.task,
            organization: value,
            currency: value.currencies?.find((currency) => currency.isDefault) || null,
            rate: value.currencies?.find((currency) => currency.isDefault)?.latestRate?.rate || 1
          };
          this.updateState({ task });
        });
    } else if (key === 'currency') {
      this.taskService.patch({ id: this.getState.task?.id, currency: value || null }).subscribe(() => {
        const task: TaskFindInterface = {
          ...this.getState.task,
          currency: value || null,
          rate: value?.latestRate?.rate || 1
        };
        this.updateState({ task });
      });
    } else {
      this.taskService.patch({ id: this.getState.task?.id, [key]: value || null }).subscribe();
      const task: TaskFindInterface = { ...this.getState.task, [key]: value || null };
      this.updateState({ task });
    }
  }

  public setDeletingLoading(loading: boolean) {
    this.updateState((state) => ({ task: loading ? { ...state.task } : null, loading }));
  }

  public updateMultipleDate(date: Date, withOrdersUpdate: boolean = false, oldDate: Date = null) {
    const task: TaskFindInterface = { ...this.getState.task };
    if (withOrdersUpdate) {
      const diff = moment(date).diff(moment(oldDate), 'days');
      task.orders = task.orders.map((order) => ({
        ...order,
        date: order.date ? (moment(order.date).add(diff, 'days') as any as Date) : date
      }));
    }
    this.taskService.moveMultiplePaymentDate({ taskIds: [task.id], date, withOrdersUpdate }).subscribe();
    this.updateState({ task });
  }

  public updateFieldTaskState(key: keyof TaskPartialUpdateFormInterface, value: any) {
    const task: TaskFindInterface = { ...this.getState.task, [key]: value || null };
    this.updateState({ task });
  }
}
